/**
 * ============================================================================
 * RejectDialog — Phase 6c frontend (CCIE).
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: modal that records the rejection reason and the QA reviewer's
 *          signature bundle id. Reason is mandatory (≥ 20 chars) to preserve
 *          the audit-trail context.
 * ============================================================================ */

import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@edms/design-system';
import dialogStyles from '../AuthorWorkbench/NewDocumentForm.module.css';
import styles from './ImpactSetReview.module.css';
import { useRejectMutation } from './useRejectMutation';

const REASON_MIN = 20;

export interface RejectDialogProps {
  changeId: string;
  onClose: () => void;
  onRejected: () => void;
}

export function RejectDialog({ changeId, onClose, onRejected }: RejectDialogProps) {
  const { t } = useTranslation();
  const titleId = useId();
  const descId = useId();
  const closeRef = useRef<HTMLButtonElement | null>(null);

  const rejectM = useRejectMutation();
  const [reason, setReason] = useState('');
  const [signature, setSignature] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    closeRef.current?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (reason.trim().length < REASON_MIN) {
      setError(
        t('changeControl.impactSet.errors.reasonTooShort', {
          defaultValue: 'Rejection reason must be at least {{min}} characters.',
          min: REASON_MIN,
        }),
      );
      return;
    }
    if (!signature.trim()) {
      setError(
        t('changeControl.impactSet.errors.signatureRequired', {
          defaultValue: 'A signature bundle id is required.',
        }),
      );
      return;
    }
    try {
      await rejectM.mutateAsync({
        changeId,
        reason: reason.trim(),
        signatureBundleId: signature.trim(),
      });
      onRejected();
    } catch (e: unknown) {
      setError(
        e instanceof Error
          ? e.message
          : t('changeControl.impactSet.errors.serverFailed', {
              defaultValue: 'Server rejected the action.',
            }),
      );
    }
  }

  return (
    <div
      className={dialogStyles.backdrop}
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={dialogStyles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
      >
        <header className={dialogStyles.header}>
          <h2 id={titleId} className={dialogStyles.title}>
            {t('changeControl.impactSet.reject.title', {
              defaultValue: 'Reject change control',
            })}
          </h2>
          <p id={descId} className={dialogStyles.desc}>
            {t('changeControl.impactSet.reject.subtitle', {
              defaultValue:
                'Provide a written reason. The rejection is recorded in the audit chain and notified to the proposer.',
            })}
          </p>
        </header>

        <form className={dialogStyles.form} onSubmit={onSubmit} noValidate>
          {error && (
            <div role="alert" className={dialogStyles.errorBanner}>
              {error}
            </div>
          )}

          <div className={dialogStyles.field}>
            <label htmlFor="rej-reason" className={dialogStyles.label}>
              {t('changeControl.impactSet.reject.reasonLabel', {
                defaultValue: 'Rejection reason',
              })}
              <span className={dialogStyles.required} aria-hidden="true">
                *
              </span>
            </label>
            <textarea
              id="rej-reason"
              className={styles.dialogTextarea}
              value={reason}
              rows={4}
              onChange={(e) => setReason(e.target.value)}
              aria-invalid={reason.length > 0 && reason.trim().length < REASON_MIN}
              required
            />
          </div>

          <div className={dialogStyles.field}>
            <label htmlFor="rej-sig" className={dialogStyles.label}>
              {t('changeControl.impactSet.reject.signatureLabel', {
                defaultValue: 'Signature bundle id',
              })}
              <span className={dialogStyles.required} aria-hidden="true">
                *
              </span>
            </label>
            <input
              id="rej-sig"
              className={dialogStyles.input}
              value={signature}
              onChange={(e) => setSignature(e.target.value)}
              placeholder="sig-…"
              required
            />
          </div>

          <footer className={dialogStyles.footer}>
            <Button
              ref={closeRef}
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={rejectM.isPending}
            >
              {t('common.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button type="submit" variant="primary" isLoading={rejectM.isPending}>
              {t('changeControl.impactSet.actions.reject', {
                defaultValue: 'Reject',
              })}
            </Button>
          </footer>
        </form>
      </div>
    </div>
  );
}
